import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { notesApiService } from '../services/notesApi';
import { Evidence } from '../types/player';
import { X, Loader, Search, AlertCircle, File, CheckCircle, Paperclip, FileText, TriangleAlert, Axe } from 'lucide-react';
import { cn } from '../utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { CreatorProfile } from './CreatorProfile';
import { ConfidentialEvidencePlaceholder } from './ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from './ConfidentialBadge';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { useAuth } from '../contexts/AuthContext';
import { EmptyState } from './ui/EmptyState';
import { SkeletonLoader } from './SkeletonLoader';

interface LinkExistingEvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLink: (evidenceId: string) => Promise<void>;
  /** Optional: exclude evidence already linked (by id) */
  excludeEvidenceIds?: string[];
}

const ItemTypeIcon = ({ itemType }: { itemType: string }) => {
  switch (itemType) {
    case 'note': return <FileText className="w-3 h-3 text-blue-400" />;
    case 'warning': return <TriangleAlert className="w-3 h-3 text-yellow-400" />;
    case 'ban': return <Axe className="w-3 h-3 text-red-400" />;
    default: return null;
  }
};

export const LinkExistingEvidenceModal = ({ isOpen, onClose, onLink, excludeEvidenceIds = [] }: LinkExistingEvidenceModalProps) => {
  const [allEvidence, setAllEvidence] = useState<Evidence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);
  const { identity } = useAuth();

  useEffect(() => {
    if (isOpen) {
      fetchEvidence();
    }
  }, [isOpen]);

  const fetchEvidence = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await notesApiService.getAllEvidence();
      setAllEvidence(data);
    } catch (err) {
      setError('Failed to load evidence.');
    } finally {
      setIsLoading(false);
    }
  };

  const excludeSet = useMemo(() => new Set(excludeEvidenceIds), [excludeEvidenceIds]);

  const filteredEvidence = useMemo(() => {
    let results = allEvidence;

    // Filter out already-linked evidence
    if (excludeSet.size > 0) {
      results = results.filter(e => !excludeSet.has(e.id));
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(e =>
        e.filename.toLowerCase().includes(query) ||
        e.uploader_id.toLowerCase().includes(query) ||
        (e.item_id && e.item_id.toLowerCase().includes(query))
      );
    }

    return results;
  }, [allEvidence, searchQuery, excludeSet]);

  const handleLink = async () => {
    if (!selectedEvidenceId) return;

    setIsLinking(true);
    setError(null);
    try {
      await onLink(selectedEvidenceId);
      setLinkSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to link evidence.';
      if (message.includes('409') || message.toLowerCase().includes('already linked')) {
        setError('This evidence is already linked to this item.');
      } else {
        setError(message);
      }
    } finally {
      setIsLinking(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[99999]" style={{ zIndex: 99999 }}>
      <div
        ref={modalRef}
        className="modal-panel max-w-2xl max-h-[85vh]"
        style={{ zIndex: 99999 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-existing-evidence-title"
      >
        <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-3 mb-4 pr-8">
          <span className="icon-tile icon-tile-accent">
            <Paperclip className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h2 id="link-existing-evidence-title" className="modal-title">Link Existing Evidence</h2>
            <p className="modal-subtitle">Select evidence to link to this item.</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-9"
            placeholder="Search by filename, uploader, or item ID..."
            aria-label="Search evidence"
          />
        </div>

        {/* Content */}
        <div className="modal-body">
          {isLoading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" aria-busy="true">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-glass-200/10">
                  <SkeletonLoader className="aspect-square w-full rounded-none" />
                  <div className="p-2 space-y-1.5">
                    <SkeletonLoader className="h-3 w-3/4" />
                    <SkeletonLoader className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && !error && filteredEvidence.length === 0 && (
            <EmptyState
              bare
              icon={Paperclip}
              title={searchQuery ? 'No evidence matches your search.' : 'No evidence available.'}
              description={searchQuery ? 'Try a different filename, uploader or item ID.' : 'Evidence uploaded across the network will appear here.'}
            />
          )}

          {!isLoading && filteredEvidence.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filteredEvidence.map((item) => {
                const redacted = isEvidenceRedacted(item, identity);
                return (
                <button
                  key={`${item.id}-${item.item_id}`}
                  type="button"
                  onClick={() => setSelectedEvidenceId(item.id)}
                  aria-pressed={selectedEvidenceId === item.id}
                  className={cn(
                    "text-left rounded-lg overflow-hidden border transition-colors",
                    selectedEvidenceId === item.id
                      ? 'border-accent-400/60 bg-accent-500/10'
                      : 'border-glass-200/10 bg-glass-100/5 hover:border-glass-200/25'
                  )}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-dark-900/40 overflow-hidden">
                    {redacted ? (
                      <ConfidentialEvidencePlaceholder compact />
                    ) : item.content_type.startsWith('image/') ? (
                      <img
                        src={getEvidenceViewUrl(item.id)}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-glass-400 p-2">
                        <File className="w-7 h-7" />
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-2">
                    <div className="flex items-center gap-1">
                      <p className="text-xs font-medium text-white truncate flex-1" title={item.filename}>
                        {item.filename}
                      </p>
                      {item.confidential && !redacted && <ConfidentialBadge />}
                    </div>
                    <div className="text-xs text-glass-400 truncate">
                      <CreatorProfile creatorId={item.uploader_id} inline />
                    </div>
                    <p className="text-[11px] text-glass-400 tabular-nums">
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                    </p>
                    {item.item_id && (
                      <div className="flex items-center gap-1 mt-1 text-[11px] text-glass-300">
                        <ItemTypeIcon itemType={item.item_type} />
                        <span className="capitalize">{item.item_type}</span>
                      </div>
                    )}
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {error && (
          <div className="alert alert-danger mt-4" role="alert">
            <AlertCircle /><span>{error}</span>
          </div>
        )}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="glass-button-secondary w-full sm:w-auto touch-manipulation">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={!selectedEvidenceId || isLinking || linkSuccess}
            className="btn-primary w-full sm:w-auto touch-manipulation"
          >
            {isLinking && <><Loader className="w-4 h-4 animate-spin" /> Linking...</>}
            {linkSuccess && <><CheckCircle className="w-4 h-4" /> Linked!</>}
            {!isLinking && !linkSuccess && 'Link Evidence'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
